import type { SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  input,
} from '@angular/core';

import { fromEvent as observableFromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {
  NotificationsAnalytics,
  NotificationTarget,
  TagsAnalytics,
  TagsTarget,
} from '@core/analytics-legacy';
import { AnalyticsMode } from '@core/analytics-types';
import {
  CandidateAnalytics,
  ShareCandidatesWithContextAnalytics,
  TrackProceedOrigin,
} from '@core/candidate-util';
import { GdprAnalyticsService } from '@core/gdpr-data';
import { TranslatePipe } from '@core/i18n-legacy';
import type { OfferFull } from '@core/offers-types';
import { TasksAnalytics } from '@core/tasks-util';
import {
  BadgeComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ButtonLoadingDirective,
  CheckboxComponent,
  ConnectedOverlayDirective,
  DialogService,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtButtonVariant,
  ScrollableDirective,
  TruncateComponent,
  WindowRef,
} from '@core/user-interface-legacy';

import { BulkActionsBatchSelectComponent } from '../bulk-actions-batch-select/bulk-actions-batch-select.component';
import { BulkActionsController } from '../bulk-actions.controller';
import { BulkActionsSelectionTracker } from '../bulk-actions.selection-tracker';
import type { BulkAction, BulkActionId, BulkItemBase } from '../bulk-actions.types';
import { BulkActionType } from '../bulk-actions.types';
import type { TaskFormChangeEvent } from '../types';

export interface ActionsVisibilityBreakpoint {
  min: number;
  max: number;
  actionIds: Array<BulkActionType | BulkActionId<string>>;
}

export type BulkActionsContext<
  OfferScope extends string = string,
  ViewType extends string = string,
  RequisitionsScope extends string = string,
> =
  | {
      offer?: Pick<OfferFull, 'kind' | 'status' | 'hireAssistant'>;
      offersScope?: OfferScope;
      searchViewType?: ViewType;
      requisitionsScope?: RequisitionsScope;
    }
  | undefined;

@Component({
  selector: 'rt-bulk-actions-toolbar',
  templateUrl: './bulk-actions-toolbar.component.html',
  styleUrls: ['./bulk-actions-toolbar.component.less'],
  host: {
    '(document:keydown.escape)': '!dialog.hasDialogsOpen() ? clearSelection() : null',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    GdprAnalyticsService,
    CandidateAnalytics,
    NotificationsAnalytics,
    TagsAnalytics,
    TasksAnalytics,
  ],
  imports: [
    GroupComponent,
    BulkActionsBatchSelectComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    TruncateComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    ScrollableDirective,
    DropdownItemComponent,
    CheckboxComponent,
    IconButtonComponent,
    TranslatePipe,
    BadgeComponent,
  ],
})
export class BulkActionsToolbarComponent {
  private readonly bulkCtrl = inject(BulkActionsController);
  private readonly window = inject(WindowRef);
  private readonly elRef = inject(ElementRef);
  private readonly bulkTracker = inject(BulkActionsSelectionTracker);
  private readonly gdprAnalyticsService = inject(GdprAnalyticsService);
  private readonly candidateAnalytics = inject(CandidateAnalytics);
  private readonly notificationsAnalytics = inject(NotificationsAnalytics);
  private readonly tagsAnalytics = inject(TagsAnalytics);
  private readonly tasksAnalytics = inject(TasksAnalytics);
  private readonly shareCandidatesWithContextAnalytics = inject(
    ShareCandidatesWithContextAnalytics,
    { optional: true },
  );

  protected readonly cdRef = inject(ChangeDetectorRef);
  public readonly dialog = inject(DialogService);

  public readonly context = input<BulkActionsContext>();
  public readonly buttonMode = input<'batchSelect' | 'toggle'>('batchSelect');
  public readonly items = input<BulkItemBase[]>(undefined!);
  public readonly variant = input<Extract<RtButtonVariant, 'tertiary' | 'outline'>>('tertiary');

  public DropdownWidth = DropdownWidth;

  public active = false;
  public selectedItems: BulkItemBase[] = [];
  public visibleActions: BulkAction[] = [];
  public hiddenActions: BulkAction[] = [];
  public dropdownOpen = false;
  public dropdownHeight = 'auto';

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.bulkCtrl.active$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(active => {
      this.dropdownOpen = !active ? false : this.dropdownOpen;
      this.active = active;
      this.cdRef.detectChanges();
    });

    this.bulkCtrl.selectedItems$
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(selectedItems => {
        this.selectedItems = selectedItems;
        this.cdRef.detectChanges();
      });

    this.bulkCtrl.ready$.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(() => {
      this.recalculateActions();
    });

    observableFromEvent(this.window.nativeWindow, 'resize')
      .pipe(debounceTime(100), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.recalculateActions();
      });

    this.bulkCtrl.recalculateActions$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
      this.recalculateActions();
    });
  }

  public ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      const { top, height } = this.elRef.nativeElement.getBoundingClientRect();
      const offsetTop = top + height + 150;
      this.dropdownHeight = `calc(100vh - ${offsetTop}px)`;
      this.cdRef.detectChanges();
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const { context, items } = changes;

    if (items) {
      this.bulkTracker.items = items.currentValue || [];
    }

    if (!context?.firstChange) {
      this.recalculateActions();
    }
  }

  public isActionInProgress(action: BulkAction): boolean {
    return this.bulkCtrl.isActionInProgress(action);
  }

  // eslint-disable-next-line max-lines-per-function
  public dispatchAction(action: BulkAction): void {
    if (action.id === BulkActionType.Extend && this.gdprAnalyticsService) {
      action.analytics = () => {
        this.gdprAnalyticsService?.trackExtendRetentionPeriod('bulk', this.selectedItems.length);
      };
    }

    // eslint-disable-next-line default-case
    switch (action.id) {
      case BulkActionType.ChangeStage:
        action.analytics = () =>
          this.candidateAnalytics.trackProceed(
            TrackProceedOrigin.STAGE_SELECT,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.Disqualify:
        action.analytics = () =>
          this.candidateAnalytics.trackDisqualify(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Requalify:
        action.analytics = () =>
          this.candidateAnalytics.trackRequalify(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Delete:
        action.analytics = () =>
          this.candidateAnalytics.trackDelete(AnalyticsMode.Bulk, this.selectedItems.length);
        break;
      case BulkActionType.Follow:
        action.analytics = () => {
          const context = this.context();
          return this.notificationsAnalytics.trackFollowAction(
            context?.offer?.kind || context?.requisitionsScope || NotificationTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        };
        break;
      case BulkActionType.Unfollow:
        action.analytics = () => {
          const context = this.context();
          return this.notificationsAnalytics.trackUnfollowAction(
            context?.offer?.kind || context?.requisitionsScope || NotificationTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        };
        break;
      case BulkActionType.AddTags:
        action.analytics = () =>
          this.tagsAnalytics.trackTagAdded(
            this.context()?.offer?.kind || TagsTarget.Candidate,
            AnalyticsMode.Bulk,
            this.selectedItems.length,
          );
        break;
      case BulkActionType.AddSources:
        action.analytics = () =>
          this.tagsAnalytics.trackSourceAdded(AnalyticsMode.Bulk, this.selectedItems.length);
        break;

      case BulkActionType.AddTask:
        action.analyticsWithPayload = ({
          candidatesCount,
          task,
        }: {
          candidatesCount: number;
          task: TaskFormChangeEvent;
        }) =>
          this.tasksAnalytics.trackAddTask(
            AnalyticsMode.Bulk,
            !!task.dueDate,
            !!task.adminIds.length,
            candidatesCount,
          );
        break;

      case BulkActionType.Share:
        action.analytics = () =>
          this.shareCandidatesWithContextAnalytics?.trackClickShare(AnalyticsMode.Bulk);
        break;
    }

    this.bulkCtrl.dispatchAction({ action, offer: this.context()?.offer || null });
  }

  public toggleDropdown(open: boolean): void {
    this.dropdownOpen = open;
    this.cdRef.detectChanges();
  }

  public toggleActive(): void {
    this.bulkCtrl.toggleActive();
  }

  public clearSelection(): void {
    this.bulkCtrl.clearSelection();
  }

  public recalculateActions(): void {
    const actions = this.bulkCtrl.getBulkActions(this.context());
    const windowWidth: number = this.window.nativeWindow.innerWidth;
    const breakpointMatch = this.bulkCtrl
      .getActionsVisibilityByBreakpoint(this.context()?.searchViewType)
      .find(breakpoint => windowWidth >= breakpoint.min && windowWidth <= breakpoint.max)!;

    if (!breakpointMatch) {
      this.visibleActions = [];
      this.hiddenActions = actions;
    }

    const { visibleActions, hiddenActions } = actions.reduce(
      (acc, action) => {
        if (breakpointMatch.actionIds.includes(action.id)) {
          acc.visibleActions.push(action);
        } else {
          acc.hiddenActions.push(action);
        }
        return acc;
      },
      { visibleActions: <BulkAction[]>[], hiddenActions: <BulkAction[]>[] },
    );

    this.visibleActions = visibleActions.filter(action => this.bulkCtrl.isActionPossible(action));
    this.hiddenActions = hiddenActions.filter(action => this.bulkCtrl.isActionPossible(action));
    this.cdRef.detectChanges();
  }
}
